/**
 * Meta Pixel (Facebook Pixel) event helpers.
 *
 * All functions are safe to call on the server (they no-op when `window` is
 * not available) so they can be imported freely in client components without
 * extra guards at the call site.
 *
 * Advanced Matching: pass `userData` to `pixelTrackPurchase` / `pixelTrackLead`
 * so Meta can match events to users even before cookies are set.
 */

// ─── low-level guard ──────────────────────────────────────────────────────────

/** Safe wrapper — no-ops on server and when pixel hasn't loaded yet. */
function fbq(command: string, event?: string, data?: Record<string, unknown>) {
  if (typeof window === 'undefined' || typeof (window as any).fbq !== 'function') return;
  const fn = (window as any).fbq as Function;
  if (event !== undefined) {
    fn(command, event, data);
  } else {
    fn(command);
  }
}

function fbqInit(pixelId: string, userData?: Record<string, string>) {
  if (typeof window === 'undefined' || typeof (window as any).fbq !== 'function') return;
  ((window as any).fbq as Function)('init', pixelId, userData);
}

// ─── user data helper ─────────────────────────────────────────────────────────

export interface PixelUserData {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
  dateOfBirth?: string | null;
  gender?: string | null;
  externalId?: string | null;
}

/**
 * Normalise user data for Meta Advanced Matching.
 * Values are lower-cased; hashing is handled by the fbevents.js SDK.
 */
function buildUserData(user: PixelUserData): Record<string, string> | undefined {
  const u: Record<string, string> = {};
  if (user.email) u['em'] = user.email.trim().toLowerCase();
  if (user.phone) u['ph'] = user.phone.replace(/\D/g, '');
  if (user.firstName) u['fn'] = user.firstName.trim().toLowerCase();
  if (user.lastName) u['ln'] = user.lastName.trim().toLowerCase();
  if (user.city) u['ct'] = user.city.trim().toLowerCase();
  if (user.state) u['st'] = user.state.trim().toLowerCase();
  if (user.zipCode) u['zp'] = user.zipCode.trim();
  if (user.country) u['country'] = user.country.trim().toLowerCase();
  if (user.dateOfBirth) u['db'] = user.dateOfBirth.replace(/\D/g, ''); // YYYYMMDD
  if (user.gender) u['ge'] = user.gender.trim().toLowerCase().charAt(0); // m|f
  if (user.externalId) u['external_id'] = String(user.externalId);
  return Object.keys(u).length ? u : undefined;
}

// ─── events ───────────────────────────────────────────────────────────────────

/** PageView — fired automatically by FacebookPixel component on route change */
export function pixelTrackPageView() {
  fbq('track', 'PageView');
}

// ---------- ViewContent -------------------------------------------------------

export interface PixelViewContentParams {
  id: string | number;
  name: string;
  category?: string;
  value?: number;
  currency?: string;
}

/** ViewContent — fire when a visitor views a product (or any key page). */
export function pixelTrackViewContent({ id, name, category, value, currency = 'ARS' }: PixelViewContentParams) {
  const data: Record<string, unknown> = {
    content_ids: [String(id)],
    content_name: name,
    content_type: 'product',
  };
  if (category) data['content_category'] = category;
  if (value != null) { data['value'] = value; data['currency'] = currency; }
  fbq('track', 'ViewContent', data);
}

// ---------- AddToCart ---------------------------------------------------------

export interface PixelAddToCartParams {
  contentIds: (string | number)[];
  numItems: number;
  value: number;
  currency?: string;
}

/** AddToCart — fire when an item is added to the cart, reporting the full cart value. */
export function pixelTrackAddToCart({ contentIds, numItems, value, currency = 'ARS' }: PixelAddToCartParams) {
  fbq('track', 'AddToCart', {
    content_ids: contentIds.map(String),
    content_type: 'product',
    value,
    currency,
    num_items: numItems,
  });
}

// ---------- InitiateCheckout --------------------------------------------------

export interface PixelInitiateCheckoutParams {
  contentIds: (string | number)[];
  numItems: number;
  value: number;
  currency?: string;
}

/** InitiateCheckout — fire when the user starts the checkout flow. */
export function pixelTrackInitiateCheckout({ contentIds, numItems, value, currency = 'ARS' }: PixelInitiateCheckoutParams) {
  fbq('track', 'InitiateCheckout', {
    content_ids: contentIds.map(String),
    content_type: 'product',
    num_items: numItems,
    value,
    currency,
  });
}

// ---------- AddPaymentInfo ----------------------------------------------------

/** AddPaymentInfo — fire when the user selects a payment method. */
export function pixelTrackAddPaymentInfo() {
  fbq('track', 'AddPaymentInfo');
}

// ---------- Purchase ----------------------------------------------------------

export interface PixelPurchaseParams {
  orderId: string;
  value: number;
  currency?: string;
  contentIds?: (string | number)[];
  numItems?: number;
  user?: PixelUserData;
}

/** Purchase — the most important event. Fire after successful payment. */
export function pixelTrackPurchase({ orderId, value, currency = 'ARS', contentIds = [], numItems, user }: PixelPurchaseParams) {
  if (user) {
    const ud = buildUserData(user);
    if (ud) fbqInit('', ud);
  }
  const data: Record<string, unknown> = {
    order_id: orderId,
    value,
    currency,
    content_ids: contentIds.map(String),
    content_type: 'product',
  };
  if (numItems != null) data['num_items'] = numItems;
  fbq('track', 'Purchase', data);
}

// ---------- Lead --------------------------------------------------------------

export interface PixelLeadParams {
  value?: number;
  currency?: string;
  user?: PixelUserData;
}

/** Lead — fire when a visitor submits a form or leaves their contact info. */
export function pixelTrackLead({ value, currency = 'ARS', user }: PixelLeadParams = {}) {
  if (user) {
    const ud = buildUserData(user);
    if (ud) fbqInit('', ud);
  }
  const data: Record<string, unknown> = {};
  if (value != null) { data['value'] = value; data['currency'] = currency; }
  fbq('track', 'Lead', data);
}

// ---------- CompleteRegistration ----------------------------------------------

/** CompleteRegistration — fire when a new user account is created. */
export function pixelTrackCompleteRegistration(user?: PixelUserData) {
  if (user) {
    const ud = buildUserData(user);
    if (ud) fbqInit('', ud);
  }
  fbq('track', 'CompleteRegistration', { status: 'completed' });
}

// ---------- Contact -----------------------------------------------------------

/** Contact — fire when a user initiates contact (e.g. opens WhatsApp chat). */
export function pixelTrackContact() {
  fbq('track', 'Contact');
}
